import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../api/admin';
import { useAdminSSE } from '../hooks/useAdminSSE';
import { Eye, Globe, Tag, Users, CheckCircle, XCircle, ChevronLeft, ChevronRight, TrendingUp, Monitor, Briefcase } from 'lucide-react';

type ReferralFilter = 'all' | 'yes' | 'no';
type ConvertedFilter = 'all' | 'yes' | 'no';

export default function AdminVisitsPage() {
  const { t } = useTranslation();
  useAdminSSE();
  const [referralFilter, setReferralFilter] = useState<ReferralFilter>('all');
  const [convertedFilter, setConvertedFilter] = useState<ConvertedFilter>('all');
  const [page, setPage] = useState(1);
  const limit = 50;

  const { data: statsData } = useQuery({
    queryKey: ['admin', 'visits', 'stats'],
    queryFn: () => adminApi.getVisitStats(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'visits', referralFilter, convertedFilter, page],
    queryFn: () => adminApi.listVisits({
      page,
      limit,
      hasReferral: referralFilter === 'all' ? undefined : referralFilter === 'yes',
      converted: convertedFilter === 'all' ? undefined : convertedFilter === 'yes',
    }),
  });

  const visits = data?.visits || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const parseUserAgent = (ua?: string): string => {
    if (!ua) return '-';
    if (ua.includes('iPhone')) return 'iPhone';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPad')) return 'iPad';
    if (ua.includes('Mac')) return 'Mac';
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Linux')) return 'Linux';
    return 'Other';
  };

  const extractPath = (url?: string): string => {
    if (!url) return '-';
    try {
      const u = new URL(url);
      return u.pathname + (u.search || '');
    } catch {
      return url;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Eye className="w-8 h-8 text-primary-400" />
              {t('admin.visits.title', 'Visitor Analytics')}
            </h1>
            <p className="text-gray-400 mt-1">
              {t('admin.visits.subtitle', 'Track landing page visits and conversions')}
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/admin/users"
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              {t('admin.users.title', 'Users')}
            </Link>
            <Link
              to="/admin/affiliates"
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all flex items-center gap-2"
            >
              <Briefcase className="w-4 h-4" />
              {t('admin.affiliates.title', 'Affiliates')}
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        {statsData && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-sm">{t('admin.visits.totalVisits', 'Total Visits')}</div>
              <div className="text-2xl font-bold text-white">{statsData.totalVisits}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-sm">{t('admin.visits.withReferral', 'With Referral')}</div>
              <div className="text-2xl font-bold text-primary-400">{statsData.visitsWithReferral}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-sm">{t('admin.visits.conversions', 'Conversions')}</div>
              <div className="text-2xl font-bold text-green-400">{statsData.conversions}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-sm">{t('admin.visits.conversionRate', 'Conv. Rate')}</div>
              <div className="text-2xl font-bold text-secondary-400">{statsData.conversionRate.toFixed(1)}%</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-sm">{t('admin.visits.todayVisits', 'Today Visits')}</div>
              <div className="text-2xl font-bold text-white">{statsData.todayVisits}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-gray-400 text-sm">{t('admin.visits.todayConv', 'Today Conv.')}</div>
              <div className="text-2xl font-bold text-green-400">{statsData.todayConversions}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {/* Referral Filter */}
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400 text-sm">{t('admin.visits.hasReferral', 'Has Referral')}:</span>
              <div className="flex gap-1">
                {(['all', 'yes', 'no'] as const).map((value) => (
                  <button
                    key={value}
                    onClick={() => { setReferralFilter(value); setPage(1); }}
                    className={`px-3 py-1 text-sm rounded-lg transition-all ${
                      referralFilter === value
                        ? 'bg-primary-600 text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {value === 'all' ? t('common.all', 'All') : value === 'yes' ? t('common.yes', 'Yes') : t('common.no', 'No')}
                  </button>
                ))}
              </div>
            </div>

            {/* Converted Filter */}
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400 text-sm">{t('admin.visits.converted', 'Converted')}:</span>
              <div className="flex gap-1">
                {(['all', 'yes', 'no'] as const).map((value) => (
                  <button
                    key={value}
                    onClick={() => { setConvertedFilter(value); setPage(1); }}
                    className={`px-3 py-1 text-sm rounded-lg transition-all ${
                      convertedFilter === value
                        ? 'bg-primary-600 text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {value === 'all' ? t('common.all', 'All') : value === 'yes' ? t('common.yes', 'Yes') : t('common.no', 'No')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="text-gray-400 text-sm mb-4">
          {t('admin.visits.showing', 'Showing {{count}} of {{total}} visits', { count: visits.length, total })}
        </div>

        {/* Visits Table */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-400">
              {t('common.loading', 'Loading...')}
            </div>
          ) : visits.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              {t('admin.visits.noVisits', 'No visits found')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t('admin.visits.date', 'Date')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t('admin.visits.visitorId', 'Visitor')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t('admin.visits.referralCode', 'Referral')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t('admin.visits.landing', 'Landing')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t('admin.visits.source', 'Source')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t('admin.visits.device', 'Device')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t('admin.visits.converted', 'Converted')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {visits.map((visit) => (
                    <tr key={visit._id} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                        {formatDate(visit.createdAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs font-mono text-gray-500">
                          {visit.visitorId.substring(0, 8)}...
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {visit.referralCode ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-500/20 text-primary-400 border border-primary-500/30">
                            {visit.referralCode}
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Globe className="w-3 h-3 text-gray-500" />
                          <span className="text-gray-400 max-w-[200px] truncate" title={visit.landingUrl}>
                            {extractPath(visit.landingUrl)}
                          </span>
                          {visit.locale && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-white/10 text-gray-400">
                              {visit.locale}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {visit.utmSource || visit.referrerUrl ? (
                          <span title={visit.referrerUrl}>
                            {visit.utmSource || 'referrer'}
                          </span>
                        ) : (
                          <span className="text-gray-500">direct</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Monitor className="w-3 h-3" />
                          {parseUserAgent(visit.userAgent)}
                          {visit.screenWidth && (
                            <span className="text-xs text-gray-500">
                              {visit.screenWidth}x{visit.screenHeight}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {visit.convertedToUserId ? (
                          <div className="flex items-center gap-1 text-green-400">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs">{formatDate(visit.convertedAt!)}</span>
                          </div>
                        ) : (
                          <XCircle className="w-4 h-4 text-gray-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-white/5 border-t border-white/10">
              <div className="text-sm text-gray-400">
                {t('admin.pagination.page', 'Page {{page}} of {{total}}', { page, total: totalPages })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
